import { alpha } from '@mui/material/styles';

// Shared helpers for the DIY tracking templates (Modern, Minimal).
// Pure runtime helpers — brand-color safety, status -> progress mapping, formatting.

const FALLBACK_ACCENT = '#2563EB';
const BLACK = { r: 0, g: 0, b: 0 };
const WHITE = { r: 255, g: 255, b: 255 };

function clamp(n, lo, hi) { return Math.max(lo, Math.min(hi, n)); }

export function hexToRgb(hex) {
    if (!hex || typeof hex !== 'string') return null;
    let h = hex.trim().replace('#', '');
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    if (h.length !== 6 || /[^0-9a-fA-F]/.test(h)) return null;
    return {
        r: parseInt(h.slice(0, 2), 16),
        g: parseInt(h.slice(2, 4), 16),
        b: parseInt(h.slice(4, 6), 16),
    };
}

function rgbToHex({ r, g, b }) {
    const to = (v) => clamp(Math.round(v), 0, 255).toString(16).padStart(2, '0');
    return `#${to(r)}${to(g)}${to(b)}`;
}

function relLuminance({ r, g, b }) {
    const lin = (c) => {
        const x = c / 255;
        return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

function contrast(a, b) {
    const l1 = relLuminance(a);
    const l2 = relLuminance(b);
    const hi = Math.max(l1, l2);
    const lo = Math.min(l1, l2);
    return (hi + 0.05) / (lo + 0.05);
}

function mix(rgb, target, w) {
    return {
        r: rgb.r + (target.r - rgb.r) * w,
        g: rgb.g + (target.g - rgb.g) * w,
        b: rgb.b + (target.b - rgb.b) * w,
    };
}

// Derive a full, white-label-safe palette from ANY brand hex.
// Graft of the judges' #1 reusable idea: accents auto-corrected so they stay legible
// for neon, pastel, or muddy brand colors alike.
export function getReadableAccent(hex) {
    const rgb = hexToRgb(hex) || hexToRgb(FALLBACK_ACCENT);
    const accent = rgbToHex(rgb);
    // Text/icon color to lay ON a full-strength accent fill.
    const onAccent = contrast(rgb, WHITE) >= contrast(rgb, BLACK) ? '#FFFFFF' : '#15171C';
    // Accent used AS hairline / large UI on WHITE: darken until it clears ~3.2:1.
    let onLight = rgb;
    let guard = 0;
    while (contrast(onLight, WHITE) < 3.2 && guard < 14) {
        onLight = mix(onLight, BLACK, 0.12);
        guard += 1;
    }
    // Accent used AS small body/link text on WHITE: tighten to WCAG-AA 4.5:1.
    let onLightStrong = rgb;
    let guard2 = 0;
    while (contrast(onLightStrong, WHITE) < 4.5 && guard2 < 16) {
        onLightStrong = mix(onLightStrong, BLACK, 0.12);
        guard2 += 1;
    }
    return {
        accent,                                  // raw brand color (fills, large blocks)
        onAccent,                                // legible text on the accent fill
        accentText: rgbToHex(onLight),           // accent for hairlines / large UI on white (>=3.2:1)
        accentStrong: rgbToHex(onLightStrong),   // accent for small text / links on white (>=4.5:1)
        soft: alpha(accent, 0.12),               // tinted chip / pill background
        softer: alpha(accent, 0.06),             // page wash / hero glow
        ring: alpha(accent, 0.22),               // focus ring / pulse
        dark: rgbToHex(mix(rgb, BLACK, 0.2)),    // dark-on-brand footer band
    };
}

// Kept in sync with Order.js (single source, imported there) so the templates and the live page agree.
export function mapStatusToBucket(status) {
    const statusMapping = {
        ORDER_PLACED: 'Shipment Packed',
        PICKUP_PENDING: 'Shipment Packed',
        PICKUP_FAILED: 'Shipment Packed',
        PICKED_UP: 'Shipment In Transit',
        INTRANSIT: 'Shipment In Transit',
        OUT_FOR_DELIVERY: 'Out for delivery',
        DELIVERED: 'Delivered',
        FAILED_DELIVERY: 'Delivery Failed',
        CANCELLED_ORDER: 'Cancelled',
        RTO_REQUESTED: 'Shipment Returned',
        RTO: 'Shipment Returned',
        RTO_OUT_FOR_DELIVERY: 'Shipment Returned',
        RTO_DELIVERED: 'Shipment Returned',
        RTO_FAILED: 'Shipment Returned',
        LOST: 'Lost',
        DAMAGED: 'Lost',
        SHIPMENT_DELAYED: 'Shipment In Transit',
        CONTACT_CUSTOMER_CARE: 'Shipment In Transit',
        SHIPMENT_HELD: 'Shipment In Transit',
        RTO_INTRANSIT: 'Shipment Returned',
        OUT_FOR_PICKUP: 'Shipment Packed',
        RTO_CONTACT_CUSTOMER_CARE: 'Shipment Returned',
        RTO_SHIPMENT_DELAY: 'Shipment Returned',
        AWB_REGISTERED: 'Shipment Packed',
        EXCHANGE_PICKUP: 'Shipment In Transit',
        EXCHANGE_INTRANSIT: 'Shipment In Transit',
        EXCHANGE_DELIVERED: 'Delivered',
        MANIFESTED: 'Shipment Packed',
        'RETURN ORDER PLACED': 'Shipment In Transit',
    };
    return statusMapping[status] || status;
}

// The four canonical stages used by the horizontal progress trackers.
export const STEPS = ['Packed', 'In Transit', 'Out for Delivery', 'Delivered'];

// phase: NORMAL | DELIVERED | FAILED | RTO | CANCELLED | LOST
// step: index of the active stage (0..3); 4 == all complete (delivered)
// tone: accent | success | warning | error | neutral
export function getProgress(currentStatus) {
    const bucket = mapStatusToBucket(currentStatus);
    switch (bucket) {
        case 'Shipment Packed': return { step: 0, phase: 'NORMAL', bucket, tone: 'accent' };
        case 'Shipment In Transit': return { step: 1, phase: 'NORMAL', bucket, tone: 'accent' };
        case 'Out for delivery': return { step: 2, phase: 'NORMAL', bucket, tone: 'accent' };
        case 'Delivered': return { step: 4, phase: 'DELIVERED', bucket, tone: 'success' };
        case 'Delivery Failed': return { step: 2, phase: 'FAILED', bucket, tone: 'warning' };
        case 'Shipment Returned': return { step: 2, phase: 'RTO', bucket, tone: 'warning' };
        case 'Cancelled': return { step: 0, phase: 'CANCELLED', bucket, tone: 'neutral' };
        case 'Lost': return { step: 1, phase: 'LOST', bucket, tone: 'error' };
        default: return { step: 1, phase: 'NORMAL', bucket, tone: 'accent' };
    }
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function formatDate(d) {
    if (!d) return '';
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return '';
    return `${dt.getDate()} ${MONTHS[dt.getMonth()]} ${dt.getFullYear()}`;
}

export function formatDateTime(d) {
    if (!d) return '';
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return '';
    let h = dt.getHours();
    const m = String(dt.getMinutes()).padStart(2, '0');
    const ap = h >= 12 ? 'pm' : 'am';
    h = h % 12 || 12;
    return `${formatDate(d)}, ${h}:${m} ${ap}`;
}

export function formatINR(v) {
    if (v === null || v === undefined || v === '') return '';
    const n = Number(v);
    if (isNaN(n)) return `₹${v}`;
    try {
        return `₹${new Intl.NumberFormat('en-IN').format(n)}`;
    } catch (e) {
        return `₹${n}`;
    }
}

function sameDay(a, b) {
    return a.getFullYear() === b.getFullYear()
        && a.getMonth() === b.getMonth()
        && a.getDate() === b.getDate();
}

// order_history sorted newest-first. The lambda payload's ordering is NOT guaranteed (the legacy
// OrderTimeLine sorts explicitly for the same reason) — every template consumer must go through
// this instead of trusting the raw array's order.
export function sortedHistory(order) {
    const history = order && Array.isArray(order.order_history) ? order.order_history : [];
    return history.slice().sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
}

// Human "Arriving today / tomorrow / on <date>" or "Delivered <date>" headline parts.
// Delivered time is derived from the newest history event (we have no discrete delivered field).
export function arrivalLabel(order) {
    const { phase } = getProgress(order && order.currentStatus);
    if (phase === 'DELIVERED') {
        const newest = sortedHistory(order)[0];
        const t = (newest && newest.timestamp) || (order && order.edd);
        const d = formatDate(t);
        return { eyebrow: 'Delivered', big: d ? `Delivered on ${d}` : 'Delivered' };
    }
    if (phase === 'FAILED') return { eyebrow: 'Action needed', big: 'Delivery attempt failed', sub: "We'll reattempt your delivery soon." };
    if (phase === 'RTO') return { eyebrow: 'Returning', big: 'On its way back', sub: 'This shipment is being returned to the sender.' };
    if (phase === 'CANCELLED') return { eyebrow: 'Cancelled', big: 'Order cancelled' };
    if (phase === 'LOST') return { eyebrow: 'Delayed', big: 'Shipment delayed', sub: 'This shipment is under investigation.' };

    const edd = order && order.edd ? new Date(order.edd) : null;
    if (!edd || isNaN(edd.getTime())) return { eyebrow: 'On its way', big: 'On its way' };
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);
    if (sameDay(edd, today)) return { eyebrow: 'Arriving', big: 'Arriving today' };
    if (sameDay(edd, tomorrow)) return { eyebrow: 'Arriving', big: `Arriving tomorrow, ${formatDate(edd)}` };
    return { eyebrow: 'Arriving', big: `Arriving ${formatDate(edd)}` };
}

// Social links present on the brand, as [{ key, url }]. Icon chosen by the template.
// Merchant-typed links often lack a scheme ("proship.in") — a schemeless href is RELATIVE, so the
// browser would resolve it against the tracking page's own origin. Prepend https:// when missing.
export function toAbsoluteUrl(url) {
    const raw = String(url == null ? '' : url).trim();
    if (!raw) return null;
    if (/^https?:\/\//i.test(raw)) return raw;
    return `https://${raw}`;
}

export function brandSocials(brand) {
    if (!brand) return [];
    const out = [];
    if (brand.instaUrl) out.push({ key: 'instagram', url: toAbsoluteUrl(brand.instaUrl) });
    if (brand.fbUrl) out.push({ key: 'facebook', url: toAbsoluteUrl(brand.fbUrl) });
    if (brand.twitterUrl) out.push({ key: 'twitter', url: toAbsoluteUrl(brand.twitterUrl) });
    if (brand.linkedinUrl) out.push({ key: 'linkedin', url: toAbsoluteUrl(brand.linkedinUrl) });
    if (brand.whatsappNumber) {
        out.push({ key: 'whatsapp', url: `https://wa.me/${String(brand.whatsappNumber).replace(/[^0-9]/g, '')}` });
    }
    return out;
}
